<template>
    <div class="tw:w-full phone-input-wrapper" :data-cy="dataCy">
        <VuePhoneNumberInput
            v-model="localPhone"
            :disabled="props.disabled"
            :border-radius="4"
            :translations="translations"
            :class="{ 'phone-error': !!errorMessage }"
            @update="onUpdate"
            @blur="onBlur"
        />

        <span
            v-if="errorMessage"
            class="tw:block tw:mt-1 tw:text-xs tw:text-red-600 tw:font-medium"
        >
            {{ errorMessage }}
        </span>
    </div>
</template>

<script setup>
    import { ref, computed } from 'vue';
    import VuePhoneNumberInput from 'vue-phone-number-input';
    import 'vue-phone-number-input/dist/vue-phone-number-input.css';

    /**
     * PhoneInput — Input de teléfono con selector de país + número.
     *
     * Wrapper moderno sobre VuePhoneNumberInput con Tailwind.
     * Reemplaza al legacy PhoneInput.vue con mejor integración visual.
     *
     * @prop {String}  value            — Número de teléfono actual.
     * @prop {Boolean} [disabled]       — Deshabilitar.
     * @prop {String}  [error]          — Mensaje de error manual.
     * @prop {String}  [placeholder]    — Placeholder del input.
     * @prop {String}  [dataCy]         — data-cy del wrapper (para tests E2E
     *                                     poder distinguir entre múltiples PhoneInput
     *                                     en la misma página: p.ej. jugador vs R1).
     *
     * @emits input           — Número formateado completo (código + número).
     * @emits update:isValid  — Booleano si el número es válido.
     */
    const props = defineProps({
        value: String,
        disabled: { type: Boolean, default: false },
        error: String,
        dataCy: String,
    });

    const emit = defineEmits(['input', 'update:isValid', 'update:callingCode']);

    const localPhone = ref(props.value || null);
    const isValid = ref(false);
    const touched = ref(false);

    const errorMessage = computed(() => {
        if (props.error) return props.error;
        if (touched.value && !localPhone.value) return 'El teléfono es requerido';
        if (localPhone.value && !isValid.value) return 'Formato de teléfono no válido';
        return '';
    });

    const translations = {
        countrySelectorLabel: 'País',
        phoneNumberLabel: 'Teléfono',
        example: 'Ej: 612 345 678',
    };

    function onUpdate(info) {
        isValid.value = info.isValid;

        // Usar e164 si está disponible (formato E.164 con +), sino construir manual
        const number = info.e164
            || (info.countryCallingCode && info.nationalNumber
                ? String(info.countryCallingCode) + info.nationalNumber
                : null)
            || null;

        emit('input', number);
        emit('update:isValid', info.isValid);
        emit('update:callingCode', info.countryCallingCode || null);
    }

    function onBlur() {
        touched.value = true;
    }

</script>

<style scoped>
/* ── Estilos para VuePhoneNumberInput ──
   Usamos CSS plano en lugar de @apply porque Tailwind v4 con prefijo tw:
   no soporta @apply con el prefijo en scoped styles.                */
.phone-input-wrapper :deep(.input-tel) {
    width: 100%;
}
/* ── Forzar altura consistente con BaseInputText (tw:p-2 + text-sm) ── */
.phone-input-wrapper :deep(.country-selector),
.phone-input-wrapper :deep(.input-tel) {
    height: 38px !important;
    min-height: 38px !important;
}
.phone-input-wrapper :deep(.country-selector) {
    width: 100px !important;
    min-width: 100px !important;
}
.phone-input-wrapper :deep(.input-tel__input) {
    display: block;
    padding: 8px 8px !important;
    width: 100%;
    font-size: 0.875rem;
    line-height: 1.25rem;
    border-radius: 0.25rem;
    border: 1px solid #cbd5e1;
    background-color: #fff;
    appearance: none;
    height: 38px !important;
    min-height: 38px !important;
    outline: 2px solid transparent;
    outline-offset: 2px;
}
.phone-input-wrapper :deep(.input-tel__input:focus) {
    border-color: #60a5fa;
    outline: none;
}
.phone-input-wrapper :deep(.country-selector__input) {
    border-color: #cbd5e1;
    height: 38px !important;
    min-height: 38px !important;
    padding-left: 38px !important;
    padding-right: 22px !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    font-size: 0.875rem;
}
.phone-input-wrapper :deep(.country-selector__country-flag) {
    top: 50% !important;
    transform: translateY(-50%);
    margin: 0;
}
.phone-input-wrapper :deep(.country-selector__country-flag img) {
    position: static !important;
    display: block;
}
.phone-input-wrapper :deep(.country-selector__label),
.phone-input-wrapper :deep(.input-tel__label) {
    display: none !important;
}
/* ── Más ancho para el selector de prefijo (bandera + código) ── */
.phone-input-wrapper :deep(.select-country-container) {
    flex: 0 0 100px !important;
    width: 100px !important;
    min-width: 100px !important;
    max-width: 100px !important;
}
.phone-input-wrapper.phone-error :deep(.input-tel__input) {
    border-color: #ef4444;
    background-color: rgba(254, 242, 242, 0.5);
}
.phone-input-wrapper.phone-error :deep(.country-selector__input) {
    border-color: #ef4444;
}
/* ── El contenedor del selector debe estar por encima de labels vecinas (z-8) ── */
.phone-input-wrapper :deep(.country-selector) {
    z-index: auto;
}
.phone-input-wrapper :deep(.country-selector.has-list-open) {
    z-index: 100 !important;
}
/* ── Dropdown de países: z-index alto + estilos ── */
.phone-input-wrapper :deep(.country-selector__list) {
    z-index: 1000;
    max-height: 240px;
    margin-top: 3px;
    border: 1px solid #cbd5e1;
    border-radius: 0.3rem;
    box-shadow: 0 10px 30px rgba(15, 23, 42, 0.12);
    overflow-x: hidden;
    overflow-y: auto;
}
.phone-input-wrapper.phone-error :deep(.country-selector__list) {
    border-color: #fca5a5;
}
</style>
